import { useState } from 'react';
import type { AuthState } from '../App';
import type { AppSession, Role } from '../lib/types';
import { api } from '../lib/api';
import { PROFILE_NAME_MAX, toProfileName } from '../lib/home';
import { AppMark, Icon } from '../components/Mark';
import { EmailSignIn } from '../components/EmailSignIn';

/**
 * The front door, in the brand's voice: a charcoal bar, a pale ground, one large calm headline and
 * one card to sign in with. The Home is the door a real person uses; the demo identities and the
 * dev sign-in are folded away underneath it.
 */
export function SignInPage({ auth, onLogin }: { auth: AuthState; onLogin: (s: AppSession) => void }) {
  const { config, configError, busy, error } = auth;
  const homeHost = config?.home.origin ? safeHost(config.home.origin) : null;
  const [name, setName] = useState('');

  return (
    <div className="landing">
      <header className="landing-bar">
        <div className="landing-bar-inner">
          <span className="brand">
            <AppMark size={22} />
            <span>OncoCoach</span>
          </span>
          <span className="hint light small">Cardiac coaching, every day</span>
        </div>
      </header>

      <main className="landing-main">
        <div className="landing-copy">
          <span className="eyebrow">Someone on your side, between appointments.</span>
          <h1>
            Care that keeps going <em>between visits.</em>
          </h1>
          <p className="lede">
            OncoCoach checks in each day, keeps your medications and readings on track, and gives your care team what
            they need to act early. Prescribed by your oncology team, free through your practice.
          </p>
          <div className="stat-row">
            <div>
              <strong>Free</strong>
              <span>through your practice, for as long as you are enrolled</span>
            </div>
            <div>
              <strong>24/7</strong>
              <span>an AI coach that knows your plan and escalates when it matters</span>
            </div>
            <div>
              <strong>&lt; 1 min</strong>
              <span>a day to log a reading and tick off your doses</span>
            </div>
          </div>
        </div>

        <div className="auth-card">
          {auth.notice ? (
            <p className="notice" role="status">
              {auth.notice}
            </p>
          ) : null}
          {error ? (
            <div className="form-error" role="alert">
              <p>{error}</p>
              <button className="quiet small" type="button" onClick={auth.dismissError}>
                dismiss
              </button>
            </div>
          ) : null}

          {configError ? (
            <>
              <h2>Sign in</h2>
              <p className="hint">OncoCoach did not answer, so we cannot tell which sign-in this deployment accepts.</p>
              <div className="form-error">{configError}</div>
              <button className="primary full" type="button" onClick={() => location.reload()}>
                Try again
              </button>
            </>
          ) : !config ? (
            <p className="hint">Checking how you sign in…</p>
          ) : (
            <>
              <h2>Sign in</h2>
              <p className="hint">Use your Home. No password to set here, and no key ever leaves your side.</p>
              <label className="field">
                <span>What should we call you?</span>
                <input type="text" value={name} maxLength={PROFILE_NAME_MAX} autoComplete="name" placeholder="optional" onChange={(e) => setName(toProfileName(e.target.value))} disabled={busy} />
              </label>
              <button className="primary big full" type="button" onClick={() => auth.signInWithHome(name)} disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in with your Home'}
              </button>
              <p className="hint small center" style={{ marginTop: '0.75rem' }}>
                A phone number, an email or a social account at {homeHost ?? 'your Home'}.
              </p>
              <EmailSignIn />
              {auth.personas.length > 0 ? <DemoUsers auth={auth} homeHost={homeHost} /> : null}
              {config.devAuth ? <DevLogin onLogin={onLogin} /> : null}
            </>
          )}
        </div>
      </main>

      <footer className="landing-foot">
        <div className="landing-foot-inner">
          <span className="powered">
            <span>Powered by</span>
            <strong>Cardiovascular Associates of America</strong>
          </span>
          <span className="hint small">OncoCoach supports your care. It never replaces your oncology team.</span>
        </div>
      </footer>
    </div>
  );
}

function safeHost(origin: string): string | null {
  try {
    return new URL(origin).host;
  } catch {
    return null;
  }
}

function DemoUsers({ auth, homeHost }: { auth: AuthState; homeHost: string | null }) {
  return (
    <details className="fold">
      <summary>Try it as someone else</summary>
      <p className="hint small">{homeHost ?? 'The Home'} lends these accounts to anyone who asks. They are real, and shared, so treat anything you do with one as public.</p>
      {auth.demoError ? (
        <div className="form-error" role="alert">
          {auth.demoError}
        </div>
      ) : null}
      <ul className="persona-list">
        {auth.personas.map((p) => (
          <li key={p.handle}>
            <button type="button" className="persona" disabled={auth.demoBusy != null || auth.busy} onClick={() => auth.connectAsDemo(p.handle)} title={p.sa}>
              <span className="persona-name">{p.name}</span>
              <span className="persona-handle">{p.handle}</span>
              <span className="persona-go">{auth.demoBusy === p.handle ? 'connecting…' : 'connect'}</span>
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Dev-only: a name and a role. Proves nothing; only where the API says DEV_AUTH is on. */
function DevLogin({ onLogin }: { onLogin: (s: AppSession) => void }) {
  const [name, setName] = useState('Sarah Johnson');
  const [role, setRole] = useState<Role>('patient');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <details className="fold" open>
      <summary>Development sign-in</summary>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          const n = name.trim();
          if (!n) return;
          setBusy(true);
          setErr(null);
          try {
            const s = await api.devLogin(n, role);
            onLogin({ token: s.token, personId: s.personId, name: s.name, via: 'dev' });
          } catch (ex) {
            setErr(ex instanceof Error ? ex.message : String(ex));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="hint small">On this development deployment only: a name and a role, seated in the demo practice (Atria Heart). No proof of anything.</p>
        <label className="field">
          <span>Name</span>
          <input type="text" value={name} maxLength={32} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="segmented" role="radiogroup" aria-label="Role">
          {(['patient', 'doctor', 'manager'] as const).map((r) => (
            <button key={r} type="button" role="radio" aria-checked={role === r} className={role === r ? 'on' : ''} onClick={() => setRole(r)}>
              {r === 'manager' ? 'Clinical manager' : r === 'doctor' ? 'Doctor' : 'Patient'}
            </button>
          ))}
        </div>
        {err ? <div className="form-error">{err}</div> : null}
        <button className="secondary full" type="submit" disabled={busy || !name.trim()}>
          {busy ? 'Signing in…' : `Enter as a ${role === 'manager' ? 'clinical manager' : role}`}
        </button>
        <span className="badge" style={{ marginTop: '0.6rem' }}>
          <Icon name="alert" size={12} /> Test data only
        </span>
      </form>
    </details>
  );
}
